import { Table } from '@ospace/shared'
import { ColumnDef } from '@tanstack/react-table'
import { Link } from 'react-router-dom'

import { deduplicationKindConfig, type DeduplicationKind } from '../config'
import type { TraceSearchResult } from '../types'
import { formatDateTime } from '../utils/format'
import { groupDetailPath } from '../utils/paths'
import { ActionBadge, TraceStatusBadge } from './StatusBadge'

const buildColumns = (kind: DeduplicationKind): ColumnDef<TraceSearchResult>[] => {
  const kindConfig = deduplicationKindConfig[kind]
  const groupLink = (row: TraceSearchResult) => groupDetailPath(kind, row.runId, row.traceId)

  return [
    {
      accessorKey: 'startedAt',
      header: 'Date',
      cell: (info) => formatDateTime(info.getValue() as string),
    },
    {
      accessorKey: 'companyName',
      header: kindConfig.entityLabel,
      cell: (info) => (
        <Link to={groupLink(info.row.original)} className='text-dark fw-bolder text-hover-primary'>
          {info.getValue() as string}
        </Link>
      ),
    },
    {
      accessorKey: 'domain',
      header: kindConfig.secondaryFieldLabel,
      cell: (info) => (info.getValue() as string | null) ?? '—',
    },
    {
      accessorKey: 'companyId',
      header: 'HubSpot ID',
    },
    {
      accessorKey: 'decidedAction',
      header: 'Decision',
      cell: (info) => <ActionBadge action={info.getValue() as string | null} />,
    },
    {
      accessorKey: 'decidedRule',
      header: 'Reason',
      cell: (info) => (info.getValue() as string | null) ?? '—',
    },
    {
      accessorKey: 'traceStatus',
      header: 'Status',
      cell: (info) => (
        <TraceStatusBadge status={info.getValue() as TraceSearchResult['traceStatus']} />
      ),
    },
    {
      id: 'trace',
      header: '',
      cell: (info) => (
        <Link to={groupLink(info.row.original)} className='btn btn-sm btn-light-primary'>
          View group
        </Link>
      ),
    },
  ]
}

type Props = {
  kind: DeduplicationKind
  results: TraceSearchResult[]
  total: number
  isLoading?: boolean
}

export const SearchResultsTable = ({ kind, results, total, isLoading }: Props) => (
  <div className='card'>
    <div className='card-header border-0 pt-5'>
      <h3 className='card-title'>
        <span className='card-label fw-bolder fs-3'>
          {total} result{total === 1 ? '' : 's'}
        </span>
      </h3>
    </div>
    <div className='card-body py-3'>
      <div className='table-responsive'>
        <Table
          columns={buildColumns(kind)}
          data={results}
          isLoading={isLoading}
          hideSearch
          hidePagination
        />
      </div>
    </div>
  </div>
)
